import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { boxApi } from '../utils/api'

export default function BoxList() {
  const [boxes, setBoxes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadBoxes()
  }, [])

  const loadBoxes = async () => {
    try {
      setLoading(true)
      const data = await boxApi.getAll()
      setBoxes(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading boxes...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <p className="text-red-800">Error loading boxes: {error}</p>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">My Boxes</h1>
          <p className="mt-2 text-sm text-gray-700">
            A list of all your rotating savings boxes.
          </p>
        </div>
        {boxes.length > 0 && (
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <Link
              to="/boxes/new"
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:w-auto"
            >
              Create Box
            </Link>
          </div>
        )}
      </div>

      {boxes.length === 0 ? (
        <div className="text-center mt-12">
          <p className="text-gray-500 mb-4">You don&apos;t have any boxes yet.</p>
          <Link
            to="/boxes/new"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-sm hover:shadow-md transition-all"
          >
            Create Your First Box
          </Link>
        </div>
      ) : (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {boxes.map((box) => (
            <Link
              key={box.id}
              to={`/boxes/${box.id}`}
              className="group bg-white overflow-hidden shadow-sm rounded-2xl border border-gray-100 hover:shadow-lg hover:border-primary-100 transition-all duration-200"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 font-semibold group-hover:bg-primary-600 group-hover:text-white transition-colors">
                      {box.name.charAt(0)}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 truncate group-hover:text-primary-600 transition-colors">
                      {box.name}
                    </h3>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      box.status === 'active'
                        ? 'bg-green-50 text-green-700 border border-green-100'
                        : box.status === 'pending'
                        ? 'bg-yellow-50 text-yellow-700 border border-yellow-100'
                        : 'bg-gray-50 text-gray-700 border border-gray-100'
                    }`}
                  >
                    {box.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-6 line-clamp-2 h-10">
                  {box.description || 'No description provided.'}
                </p>
                <div className="grid grid-cols-3 gap-4 border-t border-gray-50 pt-4">
                  <div className="text-center">
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-medium">Amount</p>
                    <p className="mt-1 font-semibold text-gray-900">
                      ${box.contribution_amount}
                    </p>
                  </div>
                  <div className="text-center border-l border-gray-50">
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-medium">Members</p>
                    <p className="mt-1 font-semibold text-gray-900">
                      {box.member_count ?? 0}
                    </p>
                  </div>
                  <div className="text-center border-l border-gray-50">
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-medium">Months</p>
                    <p className="mt-1 font-semibold text-gray-900">
                      {box.cycle_duration_months}
                    </p>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
